import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { Suspense, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "@/auth/AuthContext";
import RequireAuth from "@/auth/RequireAuth";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";

// Marketing chrome + Home are eagerly loaded — they're on the critical path.
import MarketingLayout from "@/components/MarketingLayout";
import Home from "@/pages/Home";

// Everything else is lazy. Each chunk is fetched on demand.
const HowItWorks         = lazy(() => import("@/pages/HowItWorks"));
const HowItFits          = lazy(() => import("@/pages/HowItFits"));
const TryRefills         = lazy(() => import("@/pages/TryRefills"));
const DenialsHub         = lazy(() => import("@/pages/DenialsHub"));
const DenialsByPayer     = lazy(() => import("@/pages/DenialsByPayer"));
const Modules            = lazy(() => import("@/pages/Modules"));
const Pricing            = lazy(() => import("@/pages/Pricing"));
const Hipaa              = lazy(() => import("@/pages/Hipaa"));
const About              = lazy(() => import("@/pages/About"));
const Customers          = lazy(() => import("@/pages/Customers"));
const Contact            = lazy(() => import("@/pages/Contact"));
const Demo               = lazy(() => import("@/pages/Demo"));
const Blog               = lazy(() => import("@/pages/Blog"));
const Architecture       = lazy(() => import("@/pages/Architecture"));
const Learn              = lazy(() => import("@/pages/Learn"));
const Validation         = lazy(() => import("@/pages/Validation"));
const Outcomes           = lazy(() => import("@/pages/Outcomes"));
const PayerIntelligence  = lazy(() => import("@/pages/PayerIntelligence"));
const Faq                = lazy(() => import("@/pages/Faq"));
const BillingResult      = lazy(() => import("@/pages/BillingResult"));
const WhatsNew           = lazy(() => import("@/pages/WhatsNew"));
const WorksWithEpic      = lazy(() => import("@/pages/WorksWithEpic"));
const Compare            = lazy(() => import("@/pages/Compare"));
const CompareVsAthena    = lazy(() => import("@/pages/CompareVsAthena"));
const CompareVsCerner    = lazy(() => import("@/pages/CompareVsCerner"));
const CompareAll         = lazy(() => import("@/pages/CompareAll"));
const ModuleDetail       = lazy(() => import("@/pages/ModuleDetail"));
const Terms              = lazy(() => import("@/pages/Terms"));
const NotFound           = lazy(() => import("@/pages/NotFound"));

const SignIn             = lazy(() => import("@/pages/auth/SignIn"));
const SignUp             = lazy(() => import("@/pages/auth/SignUp"));
const VerifyEmail        = lazy(() => import("@/pages/auth/VerifyEmail"));
const ForgotPassword     = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword      = lazy(() => import("@/pages/auth/ResetPassword"));
const AcceptInvite       = lazy(() => import("@/pages/auth/AcceptInvite"));
const OnboardingWizard   = lazy(() => import("@/pages/onboarding/Wizard"));

const DashboardLayout    = lazy(() => import("@/dashboard/DashboardLayout"));
const Overview           = lazy(() => import("@/dashboard/Overview"));
const ScribeDash         = lazy(() => import("@/dashboard/ScribeDash"));
const ModuleDashboard    = lazy(() => import("@/dashboard/ModuleDashboard"));
const PriorAuthDashboard = lazy(() => import("@/dashboard/PriorAuthDashboard"));
const PatientDashboard   = lazy(() => import("@/dashboard/PatientDashboard"));
const PatientIntelligence= lazy(() => import("@/dashboard/PatientIntelligence"));
const SettingsDash       = lazy(() => import("@/dashboard/SettingsDash"));
const BillingDash        = lazy(() => import("@/dashboard/BillingDash"));
const WebhooksConsole    = lazy(() => import("@/dashboard/WebhooksConsole"));

function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" aria-live="polite" aria-busy="true" data-testid="route-fallback">
      <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#4A5A52] flex items-center gap-3">
        <span className="relative flex w-2 h-2">
          <span className="absolute inset-0 rounded-full bg-[#0E8B7E] opacity-75 animate-ping" />
          <span className="relative w-2 h-2 rounded-full bg-[#0E8B7E]" />
        </span>
        Loading…
      </div>
    </div>
  );
}

export default function App() {
  return (
    <div className="App" data-testid="app-root">
      <ErrorBoundary>
        <HelmetProvider>
          <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route element={<MarketingLayout />}>
                  <Route index element={<Home />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  <Route path="/how-it-fits" element={<HowItFits />} />
                  <Route path="/try/refills" element={<TryRefills />} />
                  <Route path="/denials" element={<DenialsHub />} />
                  <Route path="/denials/:payer/:cpt" element={<DenialsByPayer />} />
                  <Route path="/works-with-epic" element={<WorksWithEpic />} />
                  <Route path="/compare" element={<Compare />} />
                  <Route path="/compare-all" element={<CompareAll />} />
                  <Route path="/compare-vs-athena" element={<CompareVsAthena />} />
                  <Route path="/compare-vs-cerner" element={<CompareVsCerner />} />
                  <Route path="/modules" element={<Modules />} />
                  <Route path="/modules/payer-intelligence" element={<PayerIntelligence />} />
                  <Route path="/modules/:slug" element={<ModuleDetail />} />
                  <Route path="/architecture" element={<Architecture />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/hipaa" element={<Hipaa />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/customers" element={<Customers />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/demo" element={<Demo />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/learn" element={<Learn />} />
                  <Route path="/outcomes" element={<Outcomes />} />
                  <Route path="/validation" element={<Validation />} />
                  <Route path="/faq" element={<Faq />} />
                  <Route path="/billing/result" element={<BillingResult />} />
                  <Route path="/whats-new" element={<WhatsNew />} />
                  <Route path="/changelog" element={<WhatsNew />} />
                  <Route path="/terms" element={<Terms />} />
                  {/* 404 inside the marketing chrome so users keep header/footer/nav */}
                  <Route path="*" element={<NotFound />} />
                </Route>

                {/* Public auth pages */}
                <Route path="/signin" element={<SignIn />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />

                {/* Onboarding */}
                <Route path="/onboarding" element={<RequireAuth role="admin"><OnboardingWizard /></RequireAuth>} />

                {/* Dashboard */}
                <Route path="/dashboard" element={<RequireAuth><DashboardLayout /></RequireAuth>}>
                  <Route index element={<Overview />} />
                  <Route path="scribe" element={<ScribeDash />} />
                  <Route path="prior-auth" element={<PriorAuthDashboard />} />
                  <Route path="no-show" element={<ModuleDashboard slug="no-show" />} />
                  <Route path="refills" element={<ModuleDashboard slug="refills" />} />
                  <Route path="care-gaps" element={<ModuleDashboard slug="care-gaps" />} />
                  <Route path="denials" element={<ModuleDashboard slug="denials" />} />
                  <Route path="patient" element={<PatientDashboard />} />
                  <Route path="intelligence" element={<PatientIntelligence />} />
                  <Route path="learn" element={<Learn embedded />} />
                  <Route path="billing" element={<BillingDash />} />
                  <Route path="webhooks" element={<WebhooksConsole />} />
                  <Route path="settings" element={<SettingsDash />} />
                </Route>
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
        </HelmetProvider>
      </ErrorBoundary>
      <Toaster position="bottom-right" theme="light" />
    </div>
  );
}
